import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, X, Star, Globe, Repeat, Shield, Clock } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import {
  comparison,
  serviceQuality,
  customerStats,
  customerLogos,
  featureHighlights,
  migration,
  faqs,
  features,
  products,
  site,
} from '@/data/content';

function CompareValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check className="mx-auto h-5 w-5 text-green-600" />;
  if (value === false) return <X className="mx-auto h-5 w-5 text-slate-300" />;
  if (value === 'partial') return <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">Partial</span>;
  if (value === 'add-on') return <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-700">Add-on</span>;
  return <span className="font-bold text-slate-700">{value}</span>;
}

const whySwitch = [
  { icon: 'Wallet', title: 'Lower cost', text: 'ZuriHub starts at $25 per agent and replaces live chat, tickets, phone, knowledge base, and analytics tools.' },
  { icon: 'Zap', title: 'AI included', text: 'Zyra AI writes replies, summarizes threads, and deflects FAQs on every plan that needs it.' },
  { icon: 'Headphones', title: 'Built-in voice', text: 'Make, receive, and transcribe AI voice calls without buying a separate phone stack.' },
  { icon: 'Globe', title: 'Global ready', text: 'Run support for Africa, Europe, North America, and Asia from one workspace with 99.99% uptime.' },
];

const moreAdvantages = [
  { icon: 'Layers', title: 'All-in-one workspace', text: 'Inbox, tickets, voice, docs, analytics, billing, and AI — no integrations needed.' },
  { icon: 'BarChart3', title: 'Live visitor analytics', text: 'See who is on your site, replay sessions, and generate heatmaps in the same tool.' },
  { icon: 'Shield', title: 'Enterprise security', text: 'GDPR and SOC 2 ready, with SSO, audit logs, and role-based access on higher tiers.' },
  { icon: 'Clock', title: 'Minutes to launch', text: 'Connect channels, add the widget, and start supporting customers the same day.' },
  { icon: 'Lock', title: 'You own your data', text: 'Customer Data is not used to train shared AI models or sold to advertisers.' },
  { icon: 'Zap', title: 'Fast global edge', text: 'Sub-200ms API response and 99.99% uptime SLA backed by multi-region infrastructure.' },
];

export default function WhyChoose() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Why Choose ZuriHub',
      description: 'Stop juggling live chat, tickets, voice, knowledge base, and analytics. ZuriHub unifies everything into one workspace at $25/agent.',
      url: `${site.baseUrl}/why-zuri`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.slice(0, 4).map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Why Choose ZuriHub - Replace 5 Tools with One Platform"
        description="Stop juggling live chat, tickets, voice, knowledge base, and analytics. ZuriHub unifies everything into one workspace at $25/agent. See why teams switch from Intercom and Zendesk."
        path="/why-zuri"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-semibold text-slate-300 backdrop-blur">
                <Star className="h-4 w-4 text-yellow-400" /> Why ZuriHub
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                One platform that <span className="gradient-text">replaces them all</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Stop juggling five different tools. ZuriHub unifies live chat, tickets, voice, knowledge base, and analytics into one workspace that actually works for your team.
              </p>
              <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
                >
                  Start free trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/features"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
                >
                  See all features <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  <span className="text-sm text-slate-400">Free to start</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  <span className="text-sm text-slate-400">No credit card required</span>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Why%20Zuri.png"
                  alt="Why choose ZuriHub platform comparison"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="comparison-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="comparison-heading" className="section-title">How ZuriHub compares</h2>
            <p className="section-subtitle mx-auto">
              The features that matter most, compared side by side with the most common alternatives.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {comparison.rows.map((row) => (
              <div
                key={row.feature}
                className="group relative rounded-3xl border border-slate-100 bg-white p-6 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <h3 className="mb-4 text-lg font-bold text-slate-900">{row.feature}</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-2xl bg-brand-50 p-3 text-center transition group-hover:bg-brand-100">
                    <p className="text-xs font-semibold text-brand-700">ZuriHub</p>
                    <div className="mt-2 flex justify-center"><CompareValue value={row.zurihub} /></div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-3 text-center transition group-hover:bg-slate-100">
                    <p className="text-xs font-semibold text-slate-500">Intercom</p>
                    <div className="mt-2 flex justify-center"><CompareValue value={row.intercom} /></div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-3 text-center transition group-hover:bg-slate-100">
                    <p className="text-xs font-semibold text-slate-500">Zendesk</p>
                    <div className="mt-2 flex justify-center"><CompareValue value={row.zendesk} /></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-8 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
            >
              Start your comparison <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="why-switch-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="why-switch-heading" className="section-title">Why teams switch to ZuriHub</h2>
            <p className="section-subtitle mx-auto">
              The most common reasons teams move from Intercom, Zendesk, and Freshdesk.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {whySwitch.map((w) => (
              <div
                key={w.title}
                className="group relative rounded-3xl border border-slate-100 bg-white p-8 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/20 transition group-hover:scale-110">
                  <Icon name={w.icon} className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{w.title}</h3>
                <p className="mt-3 text-slate-600">{w.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-8 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-50"
            >
              Explore all features <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="all-features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="all-features-heading" className="section-title">Everything you need in one workspace</h2>
            <p className="section-subtitle mx-auto">
              No more switching between live chat, tickets, voice, docs, and analytics. The whole stack is here.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div
                key={f.title}
                className="group rounded-3xl border border-slate-100 bg-white p-6 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/20 transition group-hover:scale-110">
                  <Icon name={f.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-slate-600">{f.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-8 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
            >
              Try the full stack <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="quality-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="quality-heading" className="section-title text-white">Built for scale and trust</h2>
            <p className="section-subtitle mx-auto text-slate-300">
              Performance and compliance teams can rely on from day one.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {serviceQuality.map((q) => (
              <div
                key={q.label}
                className="group relative rounded-3xl border border-slate-700 bg-slate-800/50 p-8 text-center shadow-xl shadow-black/20 transition duration-300 hover:-translate-y-2 hover:bg-slate-800 hover:shadow-2xl"
              >
                <p className="text-4xl font-extrabold text-brand-400 transition group-hover:scale-110">{q.metric}</p>
                <p className="mt-2 font-bold text-slate-200">{q.label}</p>
                <p className="mt-2 text-sm text-slate-400">{q.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <a
              href="/book-a-demo"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900 px-8 py-3.5 text-base font-bold text-white transition hover:bg-slate-800"
            >
              Talk to sales <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="products-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="products-heading" className="section-title">The product stack that makes it possible</h2>
            <p className="section-subtitle mx-auto">
              AI, cloud, and voice — built to work together in one workspace.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            {products.map((p, i) => {
              const productIcons = ['Sparkles', 'Cloud', 'Phone'];
              const iconName = productIcons[i] ?? 'Sparkles';
              return (
                <div
                  key={p.id}
                  className="group relative rounded-3xl border border-slate-100 bg-white p-8 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
                >
                  <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/20 transition group-hover:scale-110">
                    <Icon name={iconName} className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl font-extrabold text-slate-900">{p.name}</h3>
                  <p className="mt-2 text-slate-600">{p.description}</p>
                  <ul className="mt-5 space-y-2">
                    {p.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-8 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
            >
              Start building <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="global-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="global-heading" className="section-title">Global by default</h2>
              <p className="section-subtitle">
                ZuriHub runs on a global edge network with data centers across Africa, Europe, North America, and Asia-Pacific.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 text-slate-700">
                  <Globe className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">Multi-region availability</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Clock className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">99.99% uptime SLA on Enterprise</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Shield className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">GDPR and SOC 2 ready</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Repeat className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">Automatic scaling as you grow</span>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900">Trusted by teams in</h3>
              <ul className="mt-6 space-y-3">
                {customerLogos.map((logo) => (
                  <li key={logo} className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                    <Check className="h-4 w-4 text-green-600" />
                    {logo}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="more-advantages-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="more-advantages-heading" className="section-title">More reasons to choose ZuriHub</h2>
            <p className="section-subtitle mx-auto">
              The little things that add up to a faster, safer, and simpler support experience.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {moreAdvantages.map((a) => (
              <div
                key={a.title}
                className="group relative rounded-3xl border border-slate-100 bg-white p-8 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/20 transition group-hover:scale-110">
                  <Icon name={a.icon} className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{a.title}</h3>
                <p className="mt-3 text-slate-600">{a.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-8 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
            >
              Start free trial <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="migration-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="migration-heading" className="section-title">Migrate with zero stress</h2>
            <p className="section-subtitle mx-auto">
              {migration.subtitle}
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {migration.benefits.map((b) => (
              <div
                key={b}
                className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <Check className="h-4 w-4" />
                </div>
                <p className="text-lg font-semibold text-slate-900">{b}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-slate-600">
            Free migration from {migration.from.join(', ')} on Pro and Enterprise.
          </p>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="stats-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {customerStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-5xl font-extrabold text-brand-400">{stat.value}</p>
                <p className="mt-2 text-lg font-medium text-slate-300">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="highlights-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            {featureHighlights.map((h) => (
              <div
                key={h.id}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                  <Icon name={h.icon} className="h-7 w-7" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{h.title}</h2>
                <p className="mt-3 text-slate-600">{h.summary}</p>
                <ul className="mt-6 space-y-3">
                  {h.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Common questions</h2>
          <div className="mt-10 space-y-4">
            {faqs.slice(0, 4).map((f) => (
              <div
                key={f.question}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <h3 className="font-bold text-slate-900">{f.question}</h3>
                <p className="mt-2 text-slate-600">{f.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-slate-900 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">See why teams are switching</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            Start a 14-day free trial and compare ZuriHub to your current stack.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="gradient-cta mt-8 inline-flex items-center gap-2"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
